import { asOfDateAtom } from "@/features/Metrics/state/Metrics.state";
import { useAtomValue, useSetAtom } from "jotai";
import { getPlainMetricByQuery } from "@/entities/Finance/Metric.queries";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { MetricQueryString } from "@/entities/Finance/models/MetricQueryString.model";
import { MetricConfig } from "@/features/Metrics/Metrics.types";
import { formatMetricValue } from "@/features/Metrics/helpers/formatMetricValue";
import clsx from "clsx";
import {
    AIMContextViewAtom,
    AIMViewType,
} from "../../../panels/AIMPanelContext";
import { EmptyMetricMessageType } from "../../../panels/EmptyMetricPanel";
import { useMemo } from "react";

export function MetricValue({
    query,
    config,
    panelTitle,
}: {
    query: MetricQueryString;
    config: MetricConfig;
    panelTitle?: string;
}) {
    const setAIMContextView = useSetAtom(AIMContextViewAtom);
    const asOfDate = useAtomValue(asOfDateAtom);
    const { data: metric, isLoading } = useApiQuery(getPlainMetricByQuery, {
        params: {
            metricQuery: query,
            fromDate: asOfDate
                .subtract(12, "month")
                .startOf("month")
                .toISOString(),
            toDate: asOfDate.toISOString(),
        },
    });
    const depth = useMemo(() => {
        return !query.hasWindow || query.windowSize === 1 ? 0 : 1;
    }, [metric]);

    if (isLoading) {
        return (
            <div className="flex size-full items-center">
                <div
                    className={clsx(
                        "h-5 w-full bg-slate-700",
                        "animate-pulse rounded",
                    )}
                />
            </div>
        );
    }

    return (
        <span
            className={clsx(
                "w-full",
                "body-s cursor-pointer break-words text-left",
            )}
            onClick={() => {
                metric
                    ? setAIMContextView({
                          type: AIMViewType.EVALUATED_CTX_LINEAGE,
                          props: {
                              query,
                              config,
                              title: panelTitle,
                              depth,
                          },
                      })
                    : setAIMContextView({
                          type: AIMViewType.EMPTY_METRIC,
                          props: {
                              config,
                              panelTitle,
                              messageType: EmptyMetricMessageType.NO_DATA,
                          },
                      });
            }}
        >
            {!metric && "-"}
            {metric &&
                formatMetricValue(metric.getLatestValue()?.value, config.type)}
        </span>
    );
}
